import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { SpaServiceItem } from "@/types/spa";
import { trackSpaTrayOpen } from "@/utils/analytics";

interface ServiceListContextType {
    items: SpaServiceItem[];
    addItem: (item: Omit<SpaServiceItem, "quantity">) => void;
    removeItem: (id: string) => void;
    updateQuantity: (id: string, delta: number) => void;
    clearList: () => void;
    totalItems: number;
    totalPrice: number;
    isTrayOpen: boolean;
    setIsTrayOpen: (open: boolean, source?: string, serviceId?: string, serviceName?: string) => void;
    traySource: string | null;
}

const ServiceListContext = createContext<ServiceListContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = "beatenberg_spa_service_list";

export const ServiceListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<SpaServiceItem[]>([]);
    const [isHydrated, setIsHydrated] = useState(false);

    // Load from local storage on mount
    useEffect(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            try {
                setItems(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse saved service list", e);
            }
        }
        setIsHydrated(true);
    }, []);

    // Save to local storage on change
    useEffect(() => {
        if (isHydrated) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
        }
    }, [items, isHydrated]);

    const addItem = useCallback((newItem: Omit<SpaServiceItem, "quantity">) => {
        setItems((prev) => {
            const existing = prev.find((item) => item.id === newItem.id);
            if (existing) {
                return prev.map((item) =>
                    item.id === newItem.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prev, { ...newItem, quantity: 1 }];
        });
    }, []);

    const removeItem = useCallback((id: string) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const updateQuantity = useCallback((id: string, delta: number) => {
        setItems((prev) => {
            return prev
                .map((item) => {
                    if (item.id === id) {
                        const newQty = Math.max(0, item.quantity + delta);
                        return { ...item, quantity: newQty };
                    }
                    return item;
                })
                .filter((item) => item.quantity > 0);
        });
    }, []);

    const clearList = useCallback(() => {
        setItems([]);
    }, []);

    const [isTrayOpen, setIsTrayOpenState] = useState(false);
    const [traySource, setTraySource] = useState<string | null>(null);

    // Wrapper for setIsTrayOpen with tracking
    const setIsTrayOpen = useCallback((open: boolean, source?: string, serviceId?: string, serviceName?: string) => {
        if (open) {
            const normalizedSource = source || 'unknown';
            trackSpaTrayOpen(
                normalizedSource,
                serviceId,
                serviceName
            );
            setTraySource(normalizedSource); // Store source for close event
        } else {
            setTraySource(null); // Clear source when tray closes
        }
        setIsTrayOpenState(open);
    }, []);

    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return (
        <ServiceListContext.Provider
            value={{
                items,
                addItem,
                removeItem,
                updateQuantity,
                clearList,
                totalItems,
                totalPrice,
                isTrayOpen,
                setIsTrayOpen,
                traySource,
            }}
        >
            {children}
        </ServiceListContext.Provider>
    );
};

export const useServiceList = () => {
    const context = useContext(ServiceListContext);
    if (context === undefined) {
        throw new Error("useServiceList must be used within a ServiceListProvider");
    }
    return context;
};
